/**
 * Client-side singleton. Attaches window.onerror + unhandledrejection
 * listeners, tracks user context via auth state, and forwards every
 * captured error to the shared insert path.
 *
 * Init runs once per tab from the <ObservabilityProvider> React
 * component. Direct callers use `clientLogger.error(...)` to log
 * from feature code catch blocks.
 */

'use client'

import { createBrowserClient } from '@supabase/ssr'
import type { SupabaseClient } from '@supabase/supabase-js'

import { addBreadcrumb, attachClickBreadcrumbs, attachNavigationBreadcrumbs } from './breadcrumbs'
import { getConfig, type Feature, type Severity, type UserImpact } from './config'
import { inferFeatureFromPath } from './feature-from-url'
import { insertError } from './insert'
import { isKnownBrowserNoise } from './noise'
import { scrubObject, scrubString } from './sanitize'
import { generateTraceId, getOrCreateSessionId, TRACE_HEADER } from './trace'

interface LogContext {
  feature?: Feature
  userImpact?: UserImpact
  severity?: Severity
  handled?: boolean
  componentName?: string
  action?: string
  metadata?: Record<string, unknown>
  traceId?: string
  /**
   * Serialized request body from the failing fetch — captured
   * automatically by the fetch monkey-patch when a request fails or
   * returns non-2xx. Manual callers can pass their own.
   */
  requestBody?: unknown
}

/**
 * Cache of the last request body per traceId, so the fetch patch can
 * pass it into the error log even though the failure event fires from
 * a different callsite than the request initiation. Cleared after
 * TTL.
 */
const REQUEST_BODY_CACHE = new Map<string, { url: string; body: unknown; at: number }>()
const REQUEST_BODY_TTL_MS = 30_000

/**
 * Fallback: the most recent FAILED fetch's body + URL. Component catch
 * blocks that call `clientLogger.error(err, ...)` without threading a
 * traceId still pick this up if it's fresh — a form component that
 * catches a 500 within a few seconds of the fetch failing is what we
 * want. Race risk (two concurrent failing fetches) accepted:
 * one-slot-with-URL is enough to keep the attribution obvious.
 */
let LAST_FAILED_FETCH: { url: string; body: unknown; at: number } | null = null
const LAST_FAILED_TTL_MS = 5_000

class ClientObservability {
  private supabase: SupabaseClient | null = null
  private userId: string | null = null
  private proId: string | null = null
  private userRole: string | null = null
  private initialized = false

  init(): void {
    if (this.initialized || typeof window === 'undefined') return
    this.initialized = true

    // Seed userRole from config default. Apps with dynamic role (therapist:
    // admin vs supervisor vs therapist) will overwrite via setUser once
    // their profile context resolves.
    const cfg = getConfig()
    if (cfg.defaultUserRole) this.userRole = cfg.defaultUserRole

    try {
      const url = process.env.NEXT_PUBLIC_SUPABASE_URL
      const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
      if (!url || !key) throw new Error('missing Supabase env')
      // Cast — createBrowserClient's returned type parameters conflict
      // slightly with SupabaseClient<any> even though they're structurally
      // identical. We only use .from().insert() and .auth.
      const client = createBrowserClient(url, key) as unknown as SupabaseClient
      this.supabase = client
      client.auth.onAuthStateChange((_event, session) => {
        this.userId = session?.user?.id ?? null
      })
    } catch (err) {
      console.warn('[observability] client init failed:', err)
    }

    // Window-level unhandled errors — anything that escapes React
    // boundaries + naked async throws. Feature is inferred from the
    // current pathname so the admin viewer's feature filter is useful
    // (e.g. `/book/confirm` → `booking`).
    window.addEventListener('error', (event) => {
      const err = event.error instanceof Error ? event.error : new Error(event.message || 'Unknown error')
      if (isKnownBrowserNoise(err.message)) return
      void this.error(err, {
        feature: inferFeatureFromPath(window.location.pathname),
        userImpact: 'unknown',
        componentName: 'window',
        action: 'error',
        handled: false,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      })
    })

    window.addEventListener('unhandledrejection', (event) => {
      const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
      if (isKnownBrowserNoise(err.message)) return
      void this.error(err, {
        feature: inferFeatureFromPath(window.location.pathname),
        userImpact: 'unknown',
        componentName: 'window',
        action: 'unhandledrejection',
        handled: false,
      })
    })

    attachClickBreadcrumbs()
    attachNavigationBreadcrumbs()
    this.patchFetch()
    addBreadcrumb({
      kind: 'navigation',
      message: window.location.pathname + window.location.search,
    })
  }

  /**
   * Monkey-patch window.fetch so every request auto-emits a breadcrumb
   * (URL, method, status, duration) and every FAILING request grabs
   * the request body for later attachment to an error row. Covers
   * third-party clients (Supabase, Stripe) that use fetch internally
   * — without this, breadcrumbs miss the API call that preceded a
   * crash.
   *
   * Guards against double-patching under React StrictMode / Fast
   * Refresh via a symbol tag on the wrapper.
   */
  private patchFetch(): void {
    if (typeof window === 'undefined') return
    const w = window as unknown as { fetch: typeof fetch; __obsFetchPatched?: boolean }
    if (w.__obsFetchPatched) return
    w.__obsFetchPatched = true
    const original = w.fetch.bind(w)

    w.fetch = async function patchedFetch(input, init) {
      const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase()
      const rawUrl = typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url
      const safeUrl = scrubString(rawUrl)
      const start = Date.now()

      // Snapshot body BEFORE the request runs so we can attach it if the
      // request fails. FormData / streams aren't serializable — skip.
      let capturedBody: unknown
      try {
        const body = init?.body
        if (typeof body === 'string') {
          try { capturedBody = JSON.parse(body) } catch { capturedBody = body.slice(0, 5000) }
        } else if (body && typeof (body as { toString?: () => string }).toString === 'function' && !(body instanceof FormData) && !(body instanceof Blob)) {
          // URLSearchParams etc.
          capturedBody = String(body).slice(0, 5000)
        }
      } catch { /* ignore body snapshot failures */ }

      // Cache under the trace id we inject so a subsequent error log
      // can look it up. Also GC expired entries.
      const traceId = (init?.headers && (init.headers as Headers).get?.(TRACE_HEADER))
        || (init?.headers && (init.headers as Record<string, string>)[TRACE_HEADER])
        || null
      if (capturedBody != null && typeof traceId === 'string') {
        REQUEST_BODY_CACHE.set(traceId, { url: safeUrl, body: capturedBody, at: Date.now() })
        // Cheap GC: prune entries older than TTL on every set
        for (const [k, v] of REQUEST_BODY_CACHE.entries()) {
          if (Date.now() - v.at > REQUEST_BODY_TTL_MS) REQUEST_BODY_CACHE.delete(k)
        }
      }

      try {
        const response = await original(input, init)
        const duration = Date.now() - start
        addBreadcrumb({
          kind: 'fetch',
          message: `${method} ${safeUrl} → ${response.status}`,
          data: { method, url: safeUrl, status: response.status, ms: duration },
        })
        // On non-2xx, stash the body in the "last failed" slot so a
        // subsequent component catch that logs within a few seconds
        // picks it up as requestBody.
        if (!response.ok && capturedBody != null) {
          LAST_FAILED_FETCH = { url: safeUrl, body: capturedBody, at: Date.now() }
        }
        return response
      } catch (networkErr) {
        addBreadcrumb({
          kind: 'fetch',
          message: `${method} ${safeUrl} → network error`,
          data: { method, url: safeUrl, error: 'network', ms: Date.now() - start },
        })
        if (capturedBody != null) {
          LAST_FAILED_FETCH = { url: safeUrl, body: capturedBody, at: Date.now() }
        }
        throw networkErr
      }
    }
  }

  /**
   * Look up the most recent request body captured by patchFetch. First
   * tries the trace-id keyed cache (explicit chain via trackedFetch);
   * falls back to the "last failed fetch" slot (component catch block
   * that logs without threading a traceId).
   *
   * Consumer clears the entry so a single body isn't attached to
   * multiple errors.
   */
  private takeRequestBody(traceId: string | undefined): unknown {
    if (traceId) {
      const entry = REQUEST_BODY_CACHE.get(traceId)
      if (entry) {
        REQUEST_BODY_CACHE.delete(traceId)
        return scrubObject(entry.body)
      }
    }
    if (LAST_FAILED_FETCH && Date.now() - LAST_FAILED_FETCH.at < LAST_FAILED_TTL_MS) {
      const body = LAST_FAILED_FETCH.body
      LAST_FAILED_FETCH = null
      return scrubObject(body)
    }
    return undefined
  }

  /**
   * Manually set user context. Useful when the auth state comes from a
   * React context (e.g. therapist app has both userId and proId).
   */
  setUser(ctx: { userId?: string; proId?: string; userRole?: string }): void {
    if (ctx.userId !== undefined) this.userId = ctx.userId
    if (ctx.proId !== undefined) this.proId = ctx.proId
    if (ctx.userRole !== undefined) this.userRole = ctx.userRole
  }

  /**
   * Log an error at 'error' severity by default. Returns the traceId
   * so callers can display a reference code to the user.
   */
  async error(error: unknown, context?: LogContext): Promise<string> {
    const traceId = context?.traceId || generateTraceId()
    if (!this.supabase) return traceId

    // If the caller didn't supply a body, try to pick one up from the
    // fetch cache — a preceding failing request under the same trace
    // (typically true when an error boundary catches a POST failure).
    const requestBody = context?.requestBody ?? this.takeRequestBody(traceId)

    await insertError(this.supabase, {
      error,
      severity: context?.severity || 'error',
      feature: context?.feature,
      userImpact: context?.userImpact,
      handled: context?.handled,
      traceId,
      sessionId: getOrCreateSessionId(),
      userId: this.userId ?? undefined,
      proId: this.proId ?? undefined,
      userRole: this.userRole ?? undefined,
      componentName: context?.componentName,
      action: context?.action,
      metadata: context?.metadata,
      requestBody,
    })
    return traceId
  }

  async warn(error: unknown, context?: Omit<LogContext, 'severity'>): Promise<string> {
    return this.error(error, { ...context, severity: 'warn' })
  }

  async critical(error: unknown, context?: Omit<LogContext, 'severity'>): Promise<string> {
    return this.error(error, { ...context, severity: 'critical' })
  }
}

// Lazily create a stub on the server so callers don't need to guard.
export const clientLogger =
  typeof window === 'undefined'
    ? ({
        init: () => {},
        setUser: () => {},
        error: async () => '',
        warn: async () => '',
        critical: async () => '',
      } as unknown as ClientObservability)
    : new ClientObservability()

// Re-export types callers commonly need
export type { Feature, Severity, UserImpact } from './config'
export { toReferenceCode } from './trace'
